import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { NavItem } from "@/config/navConfig";
import { useGroupedNav } from "@/hooks/useGroupedNav";
import { useSidebarStore } from "@/store/useSidebarStore";
import { IconRenderer } from "./Sidebar";

// Container drives the staggered reveal/hide of the sub-menu pills. On open the
// pills cascade in (bottom → top); on close they retract in reverse.
const subMenuContainer = {
  hidden: { transition: { staggerChildren: 0.045, staggerDirection: -1 } },
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.03 } },
};

// Each pill slides up from below the bar and springs into place.
const subMenuItem = {
  hidden: { opacity: 0, y: 28, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 520, damping: 30 },
  },
};

/**
 * Mobile / tablet primary navigation rendered as a fixed-height bottom bar
 * (only below `lg`). Top-level destinations appear as raised "3D" buttons in a
 * horizontally scrollable strip; when they overflow the screen, boxed chevron
 * arrows appear on each side to nudge the strip left/right.
 *
 * Leaf items navigate directly. Parent items (with sub-menus) instead pop their
 * children upward above the tapped item, one by one, with a spring animation.
 * The hamburger drawer remains available for the full nested menu.
 */
export const BottomNav = () => {
  // Same grouped nav as the sidebar. On the horizontal strip we don't show text
  // headers; instead the groups are clustered and separated by a thin divider.
  const { showSections, groups } = useGroupedNav();
  const items = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const { pathname } = useLocation();
  const closeDrawer = useSidebarStore((s) => s.close);

  const navRef = useRef<HTMLElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Which parent's sub-menu is open, and where to anchor it horizontally
  // (px from the nav's left edge — the pill stack is centered on this).
  const [menu, setMenu] = useState<{ href: string; left: number } | null>(null);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 2);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, items.length]);

  // Recenter the active item whenever the route changes. (The sub-menu is
  // closed at each navigation point, so no setState is needed here.)
  useEffect(() => {
    const el = scrollerRef.current;
    const active = el?.querySelector<HTMLElement>("[data-active='true']");
    active?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [pathname]);

  // Collapse the sub-menu if the strip is scrolled (its anchor would drift).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => setMenu(null);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const nudge = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.7), behavior: "smooth" });
  };

  const toggleMenu = (item: NavItem, e: React.MouseEvent<HTMLButtonElement>) => {
    const navEl = navRef.current;
    if (!navEl) return;
    const navRect = navEl.getBoundingClientRect();
    const btnRect = e.currentTarget.getBoundingClientRect();
    const center = btnRect.left + btnRect.width / 2 - navRect.left;
    const left = Math.min(Math.max(center, 96), navRect.width - 96);
    setMenu((prev) => (prev?.href === item.href ? null : { href: item.href, left }));
  };

  if (items.length === 0) return null;

  const isItemActive = (item: NavItem) =>
    pathname === item.href ||
    (item.activeOnPaths?.some((p) => pathname === p) ?? false) ||
    (item.items?.some((s) => pathname === s.href) ?? false);

  const openItem = menu ? items.find((i) => i.href === menu.href) : null;
  const subItems = openItem?.items ?? [];

  return (
    <nav
      ref={navRef}
      aria-label="Primary"
      className="lg:hidden relative shrink-0 z-40 border-t border-zinc-200/80 bg-gradient-to-b from-white to-zinc-100 shadow-[0_-4px_16px_rgba(15,118,110,0.08)] pb-[env(safe-area-inset-bottom)]"
    >
      {/* Sub-menu overlay — rendered before the bar so the bar stays on top
          (interactive) while the dim backdrop sits behind the pills. */}
      <AnimatePresence>
        {menu && subItems.length > 0 && (
          <>
            <motion.div
              key="backdrop"
              className="fixed inset-0 z-0 bg-black/20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setMenu(null)}
            />
            <motion.div
              key="pills"
              className="absolute bottom-full z-10 mb-3 flex -translate-x-1/2 flex-col items-center gap-2"
              style={{ left: menu.left }}
              variants={subMenuContainer}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {subItems.map((sub) => {
                const subActive = pathname === sub.href;
                return (
                  <motion.div key={sub.href} variants={subMenuItem}>
                    <NavLink
                      to={sub.href}
                      onClick={() => {
                        setMenu(null);
                        closeDrawer();
                      }}
                      className={cn(
                        "block whitespace-nowrap rounded-full border px-4 py-2.5 text-sm font-medium transition-transform active:translate-y-px",
                        subActive
                          ? "bg-gradient-to-tr from-brand-500 to-brand-800 text-white border-white/40 shadow-[0_6px_14px_-3px_rgba(15,118,110,0.55),inset_0_1px_0_rgba(255,255,255,0.3)]"
                          : "bg-gradient-to-b from-white to-zinc-100 text-brand-700 border-white/70 shadow-[0_4px_10px_-2px_rgba(15,118,110,0.28),0_2px_3px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.9)]",
                      )}
                    >
                      {sub.title}
                    </NavLink>
                  </motion.div>
                );
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Left edge: fade + boxed arrow (only when there's room to scroll left) */}
      {canLeft && (
        <>
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-r from-white to-transparent" />
          <button
            type="button"
            aria-label="Scroll menu left"
            onClick={() => nudge(-1)}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-20 flex h-8 w-8 items-center justify-center rounded-lg bg-white text-brand-700 border border-zinc-200 shadow-[0_2px_6px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.9)] transition-transform active:scale-95"
          >
            <ChevronLeft size={16} />
          </button>
        </>
      )}

      {/* Right edge: fade + boxed arrow */}
      {canRight && (
        <>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 bg-gradient-to-l from-white to-transparent" />
          <button
            type="button"
            aria-label="Scroll menu right"
            onClick={() => nudge(1)}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-20 flex h-8 w-8 items-center justify-center rounded-lg bg-white text-brand-700 border border-zinc-200 shadow-[0_2px_6px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.9)] transition-transform active:scale-95"
          >
            <ChevronRight size={16} />
          </button>
        </>
      )}

      <div
        ref={scrollerRef}
        className="relative z-20 flex items-stretch justify-between gap-2 overflow-x-auto scroll-smooth px-2.5 py-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {groups.map((group, gi) => (
          <Fragment key={group.key}>
            {showSections && gi > 0 && group.items.length > 0 && (
              <div
                className="mx-0.5 my-1 w-px self-stretch bg-zinc-200/70 shrink-0"
                aria-hidden="true"
              />
            )}
            {group.items.map((item) => {
          const active = isItemActive(item);
          const hasChildren = (item.items?.length ?? 0) > 0;
          const isOpen = menu?.href === item.href;
          const lifted = active || isOpen;

          const tileClass = cn(
            "shrink-0 w-[68px] flex flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-[10px] font-medium leading-tight text-center border transition-[transform,box-shadow] duration-150",
            lifted
              ? "bg-gradient-to-tr from-brand-500 to-brand-800 text-white border-white/40 -translate-y-1 shadow-[0_8px_18px_-4px_rgba(15,118,110,0.55),0_2px_4px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.35)]"
              : "bg-gradient-to-b from-white to-zinc-100 text-zinc-600 border-white/70 shadow-[0_2px_3px_rgba(0,0,0,0.07),0_4px_8px_-2px_rgba(15,118,110,0.10),inset_0_1px_0_rgba(255,255,255,0.9)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.14)]",
          );

          const inner = (
            <>
              <IconRenderer
                icon={item.icon}
                iconType={item.iconType}
                size={22}
                isActive={lifted}
                className={lifted ? "text-white" : "text-brand-700"}
              />
              <span className="w-full truncate">{item.title}</span>
            </>
          );

          // Parent → toggle the sliding sub-menu; leaf → navigate.
          return hasChildren ? (
            <button
              key={item.href}
              type="button"
              data-active={active}
              aria-haspopup="true"
              aria-expanded={isOpen}
              onClick={(e) => toggleMenu(item, e)}
              className={cn(tileClass, "cursor-pointer")}
            >
              {inner}
            </button>
          ) : (
            <NavLink
              key={item.href}
              to={item.href}
              data-active={active}
              onClick={() => {
                setMenu(null);
                closeDrawer();
              }}
              className={tileClass}
            >
              {inner}
            </NavLink>
          );
            })}
          </Fragment>
        ))}
      </div>
    </nav>
  );
};
